import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  getAllIndustries,
  getAllStages,
  uploadImage,
  uploadFile,
  updateStartupProfile
} from "../utils/api";
import { tamilNaduSubdistricts } from "../utils/tamilNaduSubdistricts";

const StartupProfileModal = ({ startupId, onComplete }) => {
  const [isRegistered, setIsRegistered] = useState(false);
  const [formData, setFormData] = useState({
    startupName: localStorage.getItem("startupName") || "",
    logoUrl: "",
    pitchDeckUrl: "",
    location: "",
    industry: "",
    website: ""
  });
  const [loading, setLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [pitchDeckUploading, setPitchDeckUploading] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [apiStages, setApiStages] = useState([]);
  const navigate = useNavigate();

  const locations = Object.keys(tamilNaduSubdistricts);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [industriesRes, stagesRes] = await Promise.all([
          getAllIndustries(),
          getAllStages()
        ]);
        setIndustries(industriesRes.map(i => i.industryName || i));
        setApiStages(stagesRes);
      } catch (error) {
        console.error("Error fetching dropdown data", error);
      }
    };
    fetchDropdownData();
  }, []);

  const handleChange = async (e) => {
    const { name, type, value, files } = e.target;

    if (type === "file") {
      setFormData((fd) => ({ ...fd, [name]: files[0] }));
    } else if (type === "tel") {
      const num = value.replace(/\D/g, "").slice(0, 10);
      setFormData((fd) => ({ ...fd, [name]: num }));
    } else {
      if (name === "startupName") {
        setFormData((fd) => ({ ...fd, [name]: value }));
        localStorage.setItem("startupName", value);
      } else {
        setFormData((fd) => ({ ...fd, [name]: value }));
      }
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const { url } = await uploadImage(file);
      setFormData(prev => ({ ...prev, logoUrl: url }));
    } catch (error) {
      toast.error("Logo upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  const handlePitchDeckUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPitchDeckUploading(true);
    try {
      const { url } = await uploadFile(file);
      setFormData(prev => ({ ...prev, pitchDeckUrl: url }));
    } catch (error) {
      toast.error("Pitch deck upload failed");
    } finally {
      setPitchDeckUploading(false);
    }
  };

  // In StartupProfileModal component
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Validate required fields
      if (!formData.startupName) throw new Error("Startup name is required");
      if (isRegistered && !formData.logoUrl) throw new Error("Logo is required");
      if (isRegistered && !formData.location) throw new Error("Location is required");
      if (isRegistered && !formData.industry) throw new Error("Industry is required");

      // Prepare the data to send
      const dataToSend = {
        ...formData,
        isRegistered: isRegistered, // This should be the state value
        status: isRegistered ? "active" : "pending"
      };

      const res = await updateStartupProfile(startupId, dataToSend);

      console.log("Profile update response:", res);
      if (res.success === true) {
        toast.success("Profile updated successfully!");
        localStorage.setItem("logoUrl", res.data.logoUrl);
        window.location.reload();
      }

      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  // Prevent closing the modal if startupName is not provided
  const handleModalClose = (e) => {
    if (!formData.startupName) {
      e.preventDefault();
      toast.error("Please provide a startup name before closing");
      return;
    }
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl p-6 w-full max-w-4xl shadow-2xl mt-40">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Complete Your Startup Profile</h2>

        {/* Enhanced Toggle Switch */}
        <div className="mb-6">
          <div className="flex items-center justify-between bg-gray-100 p-1 rounded-full">
            <button
              type="button"
              onClick={() => setIsRegistered(false)}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${!isRegistered
                ? "bg-white text-purple-600 shadow-md"
                : "text-gray-600 hover:text-gray-800"}`}
            >
              Non-Registered Company
            </button>
            <button
              type="button"
              onClick={() => setIsRegistered(true)}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${isRegistered
                ? "bg-purple-600 text-white shadow-md"
                : "text-gray-600 hover:text-gray-800"}`}
            >
              Registered Company
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2 text-center">
            {isRegistered
              ? "Registered startups must provide all required information"
              : "Non-registered startups can skip optional fields"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Startup Name (always required) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Startup Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="startupName"
              value={formData.startupName}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isRegistered ? (
                <>
                  Logo <span className="text-red-500">*</span>
                </>
              ) : "Logo (Optional)"}
            </label>
            <div className="flex items-center gap-4">
              <div className="relative w-24 h-24 rounded-full border-2 border-dashed border-gray-300 overflow-hidden">
                {formData.logoUrl ? (
                  <img
                    src={formData.logoUrl}
                    alt="Logo preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <span className="text-gray-400">Logo</span>
                  </div>
                )}
              </div>
              <div>
                <label className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md cursor-pointer hover:bg-purple-700 transition-colors">
                  <span>{logoUploading ? "Uploading..." : "Choose File"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    required={isRegistered}
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="block text-gray-700 mb-1">Pitch Deck (Optional)</label>
            <input
              type="file"
              accept=".pdf,.ppt,.pptx,.doc,.docx"
              onChange={handlePitchDeckUpload}
              className="w-full p-2 border rounded"
            />
            {pitchDeckUploading && <p className="text-sm text-gray-500">Uploading...</p>}
          </div>

          <div className="form-group">
            <label className="block text-gray-700 mb-1">
              {isRegistered ? "Location *" : "Location (Optional)"}
            </label>
            <select
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              required={isRegistered}
            >
              <option value="">Select Location</option>
              {locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="block text-gray-700 mb-1">
              {isRegistered ? "Industry *" : "Industry (Optional)"}
            </label>
            <select
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              required={isRegistered}
            >
              <option value="">Select Industry</option>
              {industries.map((ind, index) => (
                <option key={index} value={ind}>{ind}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="block text-gray-700 mb-1">Website (Optional)</label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              placeholder="https://example.com"
            />
          </div>

          <div className="flex justify-end gap-3 pt-6">
            {/* <button
              type="button"
              onClick={handleModalClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button> */}
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center min-w-[100px]"
              disabled={loading || !formData.startupName}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : "Save Profile"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StartupProfileModal;